import { z } from 'zod';
import { interviewerSchema, personSchema } from '../people/person.js';
import { personnelDateSchema } from '../people/details.js';
import { pdfTemplateVersionSchema } from '../steps/pdf/index.js';
import { rejectionReasonSchema } from '../steps/review.js';
import { auditEventSchema } from '../people/audit.js';
import { communicationStatusSchema } from '../mail/communication.js';
import { effectKindSchema } from '../steps/effects.js';
import { externalProcessPhaseSchema, externalProcessStateSchema } from '../steps/types/external-process.js';
import { lifecycleStateSchema } from '../people/lifecycle.js';
import { offerGroups } from '../hr-workflow/offers.js';
import { tokenPurposeSchema } from '../portal/tokens.js';
/**
 * The HTTP response shapes, owned here so the API builds them and the web app reads them
 * through the SAME schema (CLAUDE.md rule 3). Each object is strict: a field the server
 * starts sending that the client does not know about is a contract change, and it fails
 * here — on the server, where the handler parses its own body before returning it — rather
 * than silently reaching a browser.
 *
 * Request bodies live beside the domain they change (createPersonSchema, reviewDecisionSchema,
 * uploadIntentRequestSchema…); this file is the other direction only.
 */
/**
 * What the API says when it refuses. `error` is the sentence for the person; `issues` the
 * details when there are any; `canReissue` the portal's hint that a fresh link would help.
 * Deliberately NOT strict — a refusal may carry a `state`, `reason` or `remaining` for the
 * caller that knows to look, and an unknown extra must never turn a clear refusal into a
 * parse failure.
 */
export const apiErrorSchema = z.object({
    error: z.string().min(1),
    issues: z.array(z.string()).default([]),
    canReissue: z.boolean().optional(),
});
/**
 * The refusal for a part of the app this server package was built without (#625): a switched-off
 * section for a signed-in caller, or the candidate portal for a visitor. One fixed `reason`, so
 * the caller can tell "not available yet" from "you may not" without reading the sentence.
 * Fixed words only: it never carries anything about the request or a person.
 */
export const SECTION_UNAVAILABLE_REASON = 'section-unavailable';
export const sectionUnavailableSchema = apiErrorSchema.extend({ reason: z.literal(SECTION_UNAVAILABLE_REASON) });
export function sectionUnavailableBody(error) {
    return { error, reason: SECTION_UNAVAILABLE_REASON };
}
/** What every portal route answers while the portal is closed. No fresh link would help, so none is offered. */
export const PORTAL_UNAVAILABLE_BODY = { ...sectionUnavailableBody('This is not available yet.'), canReissue: false };
export function isSectionUnavailable(body) {
    return sectionUnavailableSchema.safeParse(body).success;
}
/* ---- People ------------------------------------------------------------------------- */
export const peopleListSchema = z.object({ people: z.array(personSchema) }).strict();
export const importResultSchema = z
    .object({
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    /** Matched an existing person and changed nothing, so nothing was written. */
    unchanged: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
})
    .strict();
/**
 * A portal link, the ONE time its values exist outside the candidate's hands. Nothing stores
 * them and no endpoint shows them again; re-issuing is the answer to a lost code.
 */
export const issuedPortalLinkSchema = z
    .object({
    token: z.string().min(1),
    accessCode: z.string().min(1),
    expiresAt: z.string().datetime(),
    revokedPrevious: z.boolean(),
})
    .strict();
/**
 * The request body for issuing a portal link. POST, never GET with a query string:
 * Application Insights records request URLs, so a token there would reach a log store.
 */
export const issuePortalLinkRequestSchema = z
    .object({
    /**
     * `reveal` shows HR the link once, to pass on by hand. `email` sends it and shows HR
     * nothing but a masked destination. There is no third option and no caller-supplied
     * recipient, subject or body — the address comes from the person's record.
     */
    delivery: z.enum(['reveal', 'email']).default('reveal'),
})
    .strict();
/**
 * The answer when HR asked for the link to be SENT rather than shown.
 *
 * There is no `token` and no `accessCode` here, and that is the point: the secrets went to the
 * candidate and there is no second copy in a browser tab or a screenshot. `maskedTo` is what
 * confirms it reached the right person without being an address.
 *
 * `delivered: false` means the link exists and did not go out. The remedy is to issue again —
 * which mints a new link and revokes this one — rather than an endpoint that reveals what was
 * already sent, because such an endpoint would be a way to read any candidate's live code.
 */
export const deliveredPortalLinkSchema = z
    .object({
    delivered: z.boolean(),
    /** The masked destination, e.g. `j•••e` at the domain on file. Null when nothing was sent. */
    maskedTo: z.string().min(1).nullable(),
    templateId: z.enum(['portal-invite', 'offer-invite', 'link-reissue']).nullable(),
    /** Why it did not go out. Authored operational text, never a provider's or an exception's. */
    reason: z.string().max(200).nullable(),
    expiresAt: z.string().datetime(),
    revokedPrevious: z.boolean(),
})
    .strict();
/** `revoked: false` means there was nothing live to revoke — the answer to a retry. */
export const portalLinkRevokedSchema = z.object({ revoked: z.boolean() }).strict();
/**
 * Where a person's portal link stands, for HR to read (06b step 3, frame `278:6492`).
 *
 * THE SECRETS ARE NOT HERE, AND THAT IS THE POINT. `issuedPortalLinkSchema` carries the token and
 * the access code because issuing is the one moment they exist to be copied; this is the status
 * afterwards, read whenever HR opens the record. Putting either on a readable endpoint would undo
 * the whole "shown once" design — the frame's own words are "The access code isn't shown again".
 * `.strict()` and a test both hold that.
 *
 * `issuedBy` is deliberately absent. The frame prints "Emailed … by Design Reviewer", and the
 * token stores the issuer's user id, not their name — and #458 asked for no raw user ids on the
 * wire. Serving that line needs a name to resolve against, which is its own ticket.
 */
export const portalLinkStatusSchema = z
    .object({
    /** `none` covers both "never issued" and "the pointer names a token that is gone". */
    state: z.enum(['none', 'active', 'expired', 'revoked']),
    issuedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    /** Null while the candidate has not opened it — which is what the frame says out loud. */
    firstUsedAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
})
    .strict();
/* ---- Review ------------------------------------------------------------------------- */
export const reviewItemSchema = z
    .object({
    instanceId: z.string().min(1),
    personId: z.string().min(1),
    personName: z.string().min(1),
    type: z.string().min(1),
    /** The PINNED heading — what the person saw, never the definition's current wording. */
    heading: z.string().min(1),
    config: z.unknown(),
    submission: z.unknown(),
    submittedAt: z.string().datetime().nullable(),
    attempt: z.number().int().nonnegative(),
    satisfies: z.array(z.string().min(1)),
    segregated: z.boolean(),
})
    .strict();
export const reviewQueueSchema = z
    .object({
    items: z.array(reviewItemSchema),
    cursor: z.string().nullable(),
    /** Surfaced so the cost of the cross-partition query is visible rather than assumed. */
    requestCharge: z.number().nonnegative(),
})
    .strict();
export const decisionResultSchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal('approved'), effects: z.array(z.string().min(1)) }).strict(),
    z.object({ state: z.literal('rejected'), reason: rejectionReasonSchema }).strict(),
]);
/* ---- Portal ------------------------------------------------------------------------- */
export const portalSessionOpenedSchema = z.object({ ok: z.literal(true) }).strict();
export const portalStepSchema = z
    .object({
    id: z.string().min(1),
    type: z.string().min(1),
    state: z.string().min(1),
    /** The PINNED presentation and config — what they were issued against, not what the definition says today. */
    heading: z.string().min(1),
    helpText: z.string(),
    config: z.unknown(),
    draft: z.object({ value: z.unknown(), savedAt: z.string().datetime(), revision: z.number().int() }).strict().nullable(),
    rejectionReason: z.string().nullable(),
    /** HR's optional words to the candidate for a step sent back; empty unless the step is `rejected`. Plain text. */
    candidateComment: z.string().default(''),
    /**
     * True when the step's current answer is "I don't have this ready right now" (#232) — sent
     * and waiting for HR's upload link, or reopened by that link. A boolean and nothing else
     * from the submission. Defaulted so an older payload parses.
     */
    uploadNotReady: z.boolean().default(false),
    /**
     * The PINNED template for a step that renders a document, with the route its bytes come
     * from. Null for every other step type, and null for a pdf-form whose template has not
     * been published yet. The field map travels here; the bytes do not.
     */
    template: z.object({ version: pdfTemplateVersionSchema, bytesUrl: z.string().min(1) }).strict().nullable().default(null),
    /**
     * Where a step whose work happens ELSEWHERE stands — the only part of an office-owned
     * step's record a candidate may see.
     *
     * A WHITELIST, not a passthrough, and that is the whole design. The external checkpoint's
     * submission also carries the confirming HR user's directory id, a confirmation timestamp
     * and an operational note HR wrote for themselves; none of that is the candidate's, and a
     * generic `submission: z.unknown()` here would have sent all of it to the least-trusted
     * audience the system has. Two closed vocabularies and nothing else.
     *
     * Null for every step type that has no such state, which is every one but the external
     * checkpoint today. It names the vocabulary of one step type in a shared schema, which
     * ADR-005 would normally avoid — accepted deliberately, because the alternative is a
     * passthrough nobody can audit.
     */
    status: z
        .object({ state: externalProcessStateSchema, phase: externalProcessPhaseSchema.nullable() })
        .strict()
        .nullable()
        .default(null),
})
    .strict();
/** The packet, as the person sees it: a first name to greet them by, and their steps. No email, no role, no tags. */
export const portalPacketSchema = z.object({ name: z.string().min(1), steps: z.array(portalStepSchema) }).strict();
export const draftSavedSchema = z.object({ revision: z.number().int(), savedAt: z.string().datetime() }).strict();
export const uploadIntentSchema = z
    .object({ uploadId: z.string().min(1), uploadUrl: z.string().url(), expiresAt: z.string().datetime() })
    .strict();
export const uploadedFileSchema = z
    .object({ uploadId: z.string().min(1), fileName: z.string().min(1), byteLength: z.number().int().nonnegative() })
    .strict();
/** HR's "Send upload link" (#232): the step is back with the person. */
export const uploadLinkRequestedSchema = z.object({ state: z.literal('rejected') }).strict();
export const submitResultSchema = z.object({ state: z.literal('submitted'), submittedAt: z.string().datetime() }).strict();
export const packetSnapshotCreatedSchema = z.object({ id: z.string().min(1), sequence: z.number().int().nonnegative() }).strict();
/* ---- Operations ------------------------------------------------------------------- */
/**
 * An effect an operator can see: what it is, whom it is for, and where it stands — the
 * envelope's status and, when a delivery has touched it, the marker's verdict and reason.
 * Identifiers only; the effect's own payload (an upload id, a plan) stays in the store.
 */
export const opsEffectSchema = z
    .object({
    envelopeId: z.string().min(1),
    personId: z.string().min(1),
    kind: effectKindSchema,
    instanceId: z.string().min(1),
    attempt: z.number().int().nonnegative(),
    enqueuedAt: z.string().datetime(),
    status: z.enum(['pending', 'done', 'failed']),
    marker: z
        .object({ state: z.enum(['running', 'done', 'parked', 'failed']), detail: z.string(), claimedAt: z.string().datetime(), completedAt: z.string().datetime().nullable() })
        .strict()
        .nullable(),
})
    .strict();
export const opsEffectsSchema = z.object({ effects: z.array(opsEffectSchema), limit: z.number().int().positive() }).strict();
/**
 * One outbound integration as an operator sees it (#61, Julio 2026-09-16: park, but loudly).
 *
 * `degraded` means somebody ASKED for live delivery (`GRAPH_MODE=live`) and this deployment
 * cannot provide it — so every effect that needs it is parking. `reason` names the setting at
 * fault and never its value.
 */
export const opsIntegrationStateSchema = z
    .object({
    mode: z.enum(['live', 'fake', 'off']),
    degraded: z.boolean(),
    reason: z.string().max(200).nullable(),
})
    .strict();
export const opsIntegrationsSchema = z
    .object({
    mail: opsIntegrationStateSchema,
    /**
     * The committed-file stores (ADR-009, #182). `live` or `off` only — never `fake`: no
     * environment resolves the in-memory store.
     */
    storage: opsIntegrationStateSchema,
    /** Any integration degraded. The same flag `/api/health` carries. */
    degraded: z.boolean(),
})
    .strict();
/** A communication as an operator sees it: identifiers, status and the safe reason — never the subject or a body. */
export const opsCommunicationSchema = z
    .object({
    id: z.string().min(1),
    personId: z.string().min(1),
    templateId: z.string().nullable(),
    status: communicationStatusSchema,
    queuedAt: z.string().datetime(),
    sentAt: z.string().datetime().nullable(),
    failureReason: z.string().nullable(),
    parkedReason: z.string().max(200).nullable().default(null),
    linkedInstanceIds: z.array(z.string().min(1)),
})
    .strict();
export const opsCommunicationsSchema = z.object({ communications: z.array(opsCommunicationSchema), limit: z.number().int().positive() }).strict();
/**
 * One page of a person's audit trail, NEWEST first (#195); `nextCursor` asks for the older
 * events, null when this page reached the first one. The audit schema itself carries
 * identifiers and closed vocabularies.
 */
export const auditTrailSchema = z
    .object({ personId: z.string().min(1), events: z.array(auditEventSchema), nextCursor: z.string().min(1).max(512).nullable() })
    .strict();
/* ---- Dashboard -------------------------------------------------------------------- */
const count = z.number().int().nonnegative();
const lifecycleCounts = z
    .object(Object.fromEntries(lifecycleStateSchema.options.map((state) => [state, count])))
    .strict();
/**
 * The staff dashboard, from authoritative queries only (docs/mvp/traceability-matrix.md § G):
 * people per lifecycle, submissions waiting for review, and the work that is stuck. Nothing
 * decorative, nothing estimated. `outstanding.counted` is the bound the stuck-work counts were
 * taken under; a count equal to it means "at least".
 */
export const dashboardSchema = z
    .object({
    computedAt: z.string().datetime(),
    people: z.object({ total: count, byLifecycle: lifecycleCounts }).strict(),
    review: z.object({ pending: count }).strict(),
    /**
     * Credentials at or near their expiry, across everyone (known-limitation #11).
     *
     * DEFINITION, stated here because a number on a dashboard is a claim. Every figure counts
     * LIVE credentials only — not rejected, not superseded — that carry an expiry date, and
     * each is classified by `expiryStatusOf` against the expiry rule its own requirement
     * declares. That is the SAME call the person's checklist makes, so a licence inside its
     * grace period is not called expired here and one inside its reminder window is not called
     * fine here. A flat window on this screen would have disagreed with the person's own file.
     *
     * `counted` is how many were classified — a count without its denominator cannot be told
     * apart from a query that silently returned nothing. `unassessable` is how many carried a
     * date but had no current rule to judge it by; they are in neither bucket and are reported
     * rather than dropped.
     *
     * All of it is computed against the same instant reported in `computedAt`, never against a
     * wall clock read separately, which would let two halves of one screen disagree.
     */
    expiry: z
        .object({
        expired: count,
        expiringSoon: count,
        counted: count,
        unassessable: count,
        /**
         * The few soonest, so the figure leads somewhere. A count tells HR that work exists
         * and not whose it is, which is the difference between a number and a task.
         *
         * Bounded on purpose: a prompt to open a person's file, not a worklist, and an
         * unbounded list of names on a landing page is a different exposure decision. Ordered
         * by expiry date ascending, so anything already lapsed sorts first. Every row is one
         * of the credentials counted above — never a later one padding the list out.
         * `requirement` is the catalog LABEL, resolved server-side, so no screen carries a
         * seeded key.
         */
        soonest: z
            .array(z
            .object({
            personId: z.string().min(1),
            personName: z.string().min(1),
            requirement: z.string().min(1),
            expiresAt: z.string().datetime(),
            expired: z.boolean(),
        })
            .strict())
            .max(5),
    })
        .strict(),
    outstanding: z
        .object({
        effects: z.object({ pending: count, parked: count, failed: count }).strict(),
        communications: z.object({ queued: count, sending: count, failed: count, parked: count }).strict(),
        counted: z.number().int().positive(),
    })
        .strict(),
    /**
     * Steps HR sent back that did not come back by their due date — frame 06b `609:26466`.
     *
     * COMPUTED, NOT STORED. `stepInstanceSchema` has no `dueAt`; the due date is the send-back
     * instant plus `CORRECTION_GRACE_DAYS`, and "overdue" is today being past it. Only rows that
     * ARE overdue are carried, so the card has nothing to draw when nothing is late and the page
     * leaves it out entirely rather than showing an empty card.
     *
     * "Only HR sees this" means not the candidate: the portal has no dashboard, and both HR roles
     * read the same figures they can already reach through a person's record.
     *
     * `daysOverdue` is counted server-side against the one `computedAt` this response was built
     * at, so the pill and the date beside it cannot disagree at midnight.
     */
    overdue: z
        .object({
        rows: z
            .array(z
            .object({
            instanceId: z.string().min(1),
            personId: z.string().min(1),
            personName: z.string().min(1),
            /** The step's heading from its PINNED definition — what HR sent back, as it read then. */
            step: z.string().min(1),
            sentBackAt: z.string().datetime(),
            dueAt: z.string().datetime(),
            daysOverdue: z.number().int().positive(),
        })
            .strict())
            .max(8),
        /**
         * How many overdue send-backs there ARE, not how many are named above.
         *
         * `rows` is capped so the card stays a prompt rather than a worklist; the heading reads
         * this. Rendering `rows.length` would print "Overdue updates · 8" when twenty had slipped,
         * which is the number HR would act on.
         */
        total: count,
        /** The window `rows` was taken from, in days — so "none" can be read as "none recently". */
        windowDays: z.number().int().positive(),
    })
        .strict(),
    /**
     * What is scheduled next, across everyone — frame 06b `288:178`, "Coming up this week".
     *
     * PLANNED MILESTONES ONLY, and only ones that have not started yet. A completed or cancelled
     * one is history; one already under way is not something to come. They are the same
     * `milestone` records a person's own HR Workflow panel shows, read through one cross-person
     * query rather than copied anywhere, so nothing here can drift from that panel.
     *
     * FACTS, NOT SENTENCES (the rule `offersSchema` states): the row carries the instant and the
     * title, never "TUE 15" or "10:00 AM", so the same response reads correctly at any width and
     * in the agency's timezone rather than the database's.
     *
     * Ordered soonest first and bounded — a landing-page card, not a calendar. The frame's own
     * fourth row falls in the following week, so this is "the next few", never a strict week
     * window; `counted` says how many were considered so a short list cannot be mistaken for a
     * query that returned nothing.
     */
    upcoming: z
        .object({
        events: z
            .array(z
            .object({
            recordId: z.string().min(1),
            personId: z.string().min(1),
            /** Resolved server-side, so no screen has to hold a roster to name one person. */
            personName: z.string().min(1),
            title: z.string().min(1),
            startsAt: z.string().datetime(),
            endsAt: z.string().datetime(),
        })
            .strict())
            .max(8),
        counted: count,
    })
        .strict(),
})
    .strict();
/**
 * GET /api/offers — every candidate's offer, grouped by where it stands (#259, frame 06b
 * `293:444`).
 *
 * FACTS, NOT SENTENCES. The row carries the dates and the terms the screen reads from; the
 * wording — "PT · W-2 · Per diem", "Accept by Sep 17, 5:00 PM", "Due in 3 days" — is the web's,
 * so the same response can be read at any width and in any wording Paul settles on.
 *
 * ONE ROW PER OFFER, not per person (#297, #321). A candidate holding a signed offer and a fresh
 * draft is two rows, in two groups, so `personId` is NOT unique across `rows` — `recordId` is the
 * row's identity. A candidate with no offer at all still gets a row, under `to-prepare`, and that
 * is the only row whose `recordId` is null.
 *
 * `counts` therefore counts ROWS, not people, and names every group including the empty ones so
 * the filters can show "Ready to send (0)" without the client inventing the zero.
 */
export const offerRowSchema = z
    .object({
    personId: z.string().uuid(),
    candidateName: z.string(),
    jobProfileKeys: z.array(z.string()),
    group: z.enum(offerGroups),
    /** The workflow record this row is about; null exactly when the group is `to-prepare`. */
    recordId: z.string().nullable(),
    /** Calendar days to the acceptance deadline; present only while out for signature. */
    dueInDays: z.number().int().nullable(),
    /**
     * How many of this person's offers are still out for signature, counted in EVERY group and
     * excluding any that has been signed.
     *
     * It is not only a figure for the out-for-signature rows. `record-existing-offer` has no guard
     * against a live portal offer, so a `signed` row can carry one — and that offer, which the
     * candidate can still open and sign, would otherwise be invisible on the one screen meant to
     * show every offer in flight (#275).
     */
    liveOffers: z.number().int().nonnegative(),
    offer: z
        .object({
        position: z.string(),
        employmentClassification: z.enum(['w2', '1099']),
        employmentType: z.string(),
        proposedStartDate: z.string(),
        acceptanceDeadline: z.string().datetime(),
    })
        .strict()
        .nullable(),
    /** How this offer reached its signature: in the portal, or recorded from a signed copy. */
    signedVia: z.enum(['portal', 'recorded']).nullable(),
    addedAt: z.string().datetime(),
    draftedAt: z.string().datetime().nullable(),
    draftedBy: z.string().nullable(),
    issuedAt: z.string().datetime().nullable(),
    signedAt: z.string().datetime().nullable(),
})
    .strict()
    .superRefine((row, ctx) => {
    /**
     * `OfferStanding` states these in prose and enforces them in the code that builds a row; the
     * wire schema did not, so a response could satisfy the schema and still contradict the type
     * it is supposed to carry (#321). They are checked HERE because this is the boundary — a
     * client parses this, and a row that breaks one of them makes the screen state something
     * untrue rather than fail.
     *
     * Each is quoted from the field it guards, not invented:
     */
    const needs = (ok, path, message) => {
        if (!ok)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    };
    // "The record the row is about; null exactly when the group is `to-prepare`."
    needs((row.recordId === null) === (row.group === 'to-prepare'), 'recordId', 'recordId is null exactly when the group is to-prepare');
    // "Non-null exactly when the group is `signed`."
    needs((row.signedAt === null) === (row.group !== 'signed'), 'signedAt', 'signedAt is set exactly when the group is signed');
    // "non-null EXACTLY when the group is `signed`."
    needs((row.signedVia === null) === (row.group !== 'signed'), 'signedVia', 'signedVia is set exactly when the group is signed');
    /*
      "Present only while an offer is out for signature." Stated one-way on purpose: the countdown
      is absent outside that group, but a row INSIDE it may still carry null — `offerStandings`
      answers null when there is no deadline to count to — so the converse is not an invariant and
      asserting it would reject valid rows.
    */
    needs(row.dueInDays === null || row.group === 'out-for-signature', 'dueInDays', 'dueInDays is present only while an offer is out for signature');
    // "NEVER NEGATIVE any more (#301)" — a passed deadline is `expired`, which has no countdown.
    needs(row.dueInDays === null || row.dueInDays >= 0, 'dueInDays', 'dueInDays is never negative: a passed deadline is expired, not a negative countdown');
});
/**
 * A candidate in hiring whose own records could not be read (#299).
 *
 * NOT AN `OfferRow` WITH NULLS. Where this person's offer stands is precisely what is unknown, so
 * there is no group to put them in, no deadline, no terms and no signature — and an `OfferRow`
 * carrying `group: 'to-prepare'` would state, on the strength of a parse failure, that nobody has
 * drafted them an offer. Every field here is one that was actually read.
 *
 * WHY A ROW AT ALL. One malformed document used to 500 the whole screen for every user; omitting
 * the candidate instead would hide a person from the one list meant to show every offer in flight,
 * which is worse than the 500 because nothing says anything is wrong. This is the same choice the
 * dashboard already makes for a name it cannot resolve ("Record unavailable", `dashboard.ts`) and
 * the review queue makes for a missing person ("Unknown person", `review.ts`): keep the row, say
 * plainly which part is missing.
 *
 * DELIBERATELY NOT IN `counts`. The five group counts are what the filter pills add up, and a
 * person whose group is unknown belongs to none of them; the array's own length is the figure.
 */
export const unreadableOfferRowSchema = z
    .object({
    /**
     * Not `.uuid()` like `offerRowSchema.personId`: when the PERSON document is the malformed one,
     * this is whatever id it carried, and refusing it here would throw away the only handle HR has
     * for opening the record and fixing it.
     */
    personId: z.string().min(1),
    /** The person's name when it was readable, and `'Record unavailable'` when it was not. */
    candidateName: z.string().min(1),
    jobProfileKeys: z.array(z.string()),
    /** Null exactly when the person document itself could not be read. */
    addedAt: z.string().datetime().nullable(),
})
    .strict();
export const offersSchema = z
    .object({
    computedAt: z.string().datetime(),
    rows: z.array(offerRowSchema),
    counts: z.object(Object.fromEntries(offerGroups.map((group) => [group, count]))).strict(),
    /** Candidates whose records could not be read. See `unreadableOfferRowSchema`. */
    unavailable: z.array(unreadableOfferRowSchema),
    /**
     * The candidate bound this response was read under (#300), reported for the reason the
     * dashboard reports `counted`: a short list must not be mistakable for a query that returned
     * nothing, and a bound nobody can see is a bound nobody notices.
     */
    counted: count,
    /** Whether the roster holds more candidates than `counted`. */
    truncated: z.boolean(),
    /**
     * Request units the whole read cost, as the review queue reports its own — so the price of
     * this screen is visible rather than assumed. This endpoint had no figure at all while being
     * the most expensive read in the app.
     */
    requestCharge: z.number().nonnegative(),
})
    .strict();
/* ---- Onboarding packet preview ------------------------------------------------------- */
/**
 * What moving this person to Onboarding would add to their portal (#264, frame 06b `322:1186`).
 *
 * Counted by step TYPE, because that is the only grouping the packet actually has. The frame
 * names six buckets — "to fill in", "to read and sign", "health choices", "PDFs to review and
 * sign", "to acknowledge", "uploads" — and four of them correspond to a step type while
 * "health choices" and the split between acknowledging and reading-and-signing do not exist in
 * the catalog at all. Inventing a mapping would put a number on the screen that nothing computes,
 * so the wire carries the honest grouping and `packetStepCopy` does the wording.
 */
export const onboardingPreviewSchema = z
    .object({
    /** Every step that would be created, summed across the groups. */
    total: z.number().int().nonnegative(),
    groups: z.array(z.object({ type: z.string().min(1), count: z.number().int().positive() }).strict()),
    /**
     * Requirements that apply to this person and that no published definition can close.
     *
     * Reported for the same reason `issuePacket` reports them: a requirement nobody can satisfy
     * looks exactly like one nobody has got to yet, and HR is about to promise the candidate a
     * packet. Better said before the move than discovered after it.
     */
    gaps: z.array(z.string().min(1)),
})
    .strict();
/* ---- Interviews & orientation (#303) -------------------------------------------------- */
/**
 * One candidate's interview facts, for the cross-person read frame 06b's nav needs (#303).
 *
 * FACTS, NOT SENTENCES, the rule `offersSchema` states: the row carries the date and the
 * interviewers, and the screen decides what "not scheduled" reads like. There is no derived
 * status here on purpose — "overdue", "this week" and "needs scheduling" are all calendar
 * questions whose answer depends on the viewer's day, and the one place that already knows the
 * agency's zone is the date schema this reuses.
 *
 * `interviewers` is the stored shape rather than a flattened list of names, because
 * `interviewerSchema.userId` is the whole point of that type (see its comment): an interviewer is
 * a person with an id so the interview can be linked to an Outlook account later, and a list of
 * strings would throw that away at the API boundary.
 */
export const interviewRowSchema = z
    .object({
    personId: z.string().uuid(),
    candidateName: z.string(),
    jobProfileKeys: z.array(z.string()),
    /** Which stage the person is at, so the screen can tell a pending interview from a past one. */
    lifecycle: lifecycleStateSchema,
    /**
     * The day the interview happened, or `null` when none is recorded — NOT "not yet happened".
     *
     * `personnelDateSchema`, the FORMAT rule, and not `candidateIntakeSchema`'s refined
     * `interviewDateSchema`. The value reaching this row came out of a `personSchema.parse`, so it
     * has already satisfied the stricter agency-zone rule; re-applying it here is a check that
     * cannot fail, and it would mean exporting a module-private const to say so. The stored rule
     * lives at the write, where `candidateIntakeSchema` keeps it, and the read's job is to report
     * faithfully what passed it.
     */
    interviewedOn: personnelDateSchema.nullable(),
    interviewers: z.array(interviewerSchema),
    /** When the candidate was added, which is what orders this page. */
    addedAt: z.string().datetime(),
})
    .strict();
/**
 * The interviews page.
 *
 * NO `unavailable` ARRAY, and that absence is deliberate rather than an omission to be filled in
 * later. `offersSchema` carries one because `data/offers.ts` parses each document separately, so
 * one malformed record degrades to one row (#299). This read goes through `PeopleStore.list`,
 * whose `peoplePageFromResponse` parses the page with `personSchema.parse` in a `.map` — one bad
 * person document rejects the whole page. Declaring `unavailable` here would advertise a
 * resilience this endpoint does not have. Making it true is a change to the shared roster read
 * that the People directory shares, so it belongs with that read and not with this screen.
 */
export const interviewsSchema = z
    .object({
    computedAt: z.string().datetime(),
    rows: z.array(interviewRowSchema),
    /** The candidate bound this response was read under, reported as `offersSchema` reports its own. */
    counted: count,
    /** Whether more candidates exist than `counted`. */
    truncated: z.boolean(),
    /** Request units the read cost, so this screen's price is visible rather than assumed. */
    requestCharge: z.number().nonnegative(),
})
    .strict();
/* ---- Portal links (#303) -------------------------------------------------------------- */
/**
 * Where ONE person's live portal link stands, for the cross-person Portal links read (#303).
 *
 * THE SAME FIVE FACTS `portalLinkStatusSchema` CARRIES, plus the person and purpose they belong to,
 * because this row is the per-person panel's answer asked of many people at once. The state rule is
 * `portalLinkState` in both places rather than written twice — a list that called a link active
 * while the record called it expired would be wrong in neither place in particular.
 *
 * IT CARRIES NO SECRET, which is the property worth stating on a cross-person read rather than a
 * per-person one: not the token, not its digest, and not the access code. `portalLinkStatusSchema`
 * already refuses to hand back what was shown once; an endpoint that listed every live link would
 * be the single most valuable thing in the app to read, so it carries timestamps and nothing else.
 *
 * THERE IS NO NAME ON THE ROW, and that is a decision rather than an omission. The name is on the
 * PERSON document and this read sees only the pointer and the token, so adding it means either a
 * second read per person — the fan-out #300 exists to remove — or a second batched query whose
 * shape the screen should decide, and the screen is listed under "Not designed yet"
 * (`docs/mvp/UIUX-FIGMA-HANDOFF.md`). `personId` is the handle; whoever draws the page decides what
 * it displays and what that costs.
 */
export const portalLinkRowSchema = z
    .object({
    /**
     * Not `.uuid()` like `interviewRowSchema.personId`, for `unreadableOfferRowSchema`'s reason:
     * this value comes off the POINTER document, and refusing an unexpected one here would reject
     * the whole page — 500ing a screen of other people's intact rows over one odd document.
     */
    personId: z.string().min(1),
    /**
     * Which link this is. Read from the pointer's own id (`active-token:{purpose}`), which is the
     * only place a pointer records it — and the pointer, not the token, is the authority on what is
     * live for a person and purpose.
     */
    purpose: tokenPurposeSchema,
    /**
     * `none` and `unreadable` are the two that are NOT about the link's life.
     *
     * `none` is `portalLinkStatusSchema`'s own: a pointer naming a token that is gone. The pointer
     * and the token are separate documents and the token carries a TTL, so the pointer outliving it
     * is ordinary, and "no link" is the true answer.
     *
     * `unreadable` has no equivalent on the per-person read, and the asymmetry is deliberate. There,
     * one token is the whole page, so failing loudly is the honest answer. Here, one malformed
     * document must not take 499 good rows with it — the failure #299 calls worse than an error —
     * so it degrades to one row that says so, as the Offers screen's `unavailable` rows do.
     */
    state: z.enum(['none', 'active', 'expired', 'revoked', 'unreadable']),
    issuedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    /** Null while the candidate has not opened it, as the per-person status says out loud. */
    firstUsedAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * The Portal links page.
 *
 * `counted` IS A POINTER BOUND, not a person bound, and that is the one figure on this response a
 * reader could misread. `tokenPurposeSchema` has two members, so one person can hold two live links
 * — their packet and an ad-hoc document request — and each is a row. At the bound this is at most
 * `counted` ROWS, which is at least `counted / 2` people.
 */
export const portalLinksSchema = z
    .object({
    computedAt: z.string().datetime(),
    rows: z.array(portalLinkRowSchema),
    /** The pointer bound this response was read under, reported as `offersSchema` reports its own. */
    counted: count,
    /** Whether more pointers exist than `counted`. */
    truncated: z.boolean(),
    /**
     * Request units the WHOLE read cost — the pointer query plus every point read it fanned out to.
     *
     * Summed rather than reported per query, because the number a reader needs is what one page view
     * costs. This endpoint is 1 + N by design (see the handler), so a charge covering only the first
     * query would hide exactly the part that scales.
     */
    requestCharge: z.number().nonnegative(),
})
    .strict();
/** Every response object above, for the contract test that pins them strict. */
export const responseSchemas = {
    onboardingPreviewSchema,
    peopleListSchema,
    importResultSchema,
    issuedPortalLinkSchema,
    portalLinkRevokedSchema,
    portalLinkStatusSchema,
    reviewItemSchema,
    reviewQueueSchema,
    portalSessionOpenedSchema,
    portalStepSchema,
    portalPacketSchema,
    draftSavedSchema,
    uploadIntentSchema,
    uploadedFileSchema,
    submitResultSchema,
    uploadLinkRequestedSchema,
    packetSnapshotCreatedSchema,
    opsEffectSchema,
    opsEffectsSchema,
    opsCommunicationSchema,
    opsCommunicationsSchema,
    auditTrailSchema,
    dashboardSchema,
    offerRowSchema,
    unreadableOfferRowSchema,
    offersSchema,
    interviewRowSchema,
    interviewsSchema,
    portalLinkRowSchema,
    portalLinksSchema,
};
//# sourceMappingURL=responses.js.map